import Button from '@/componentsDS/Button/Button';
import { ButtonType, ButtonVariant } from '@/componentsDS/Button/Button.type';
import Chips, { ChipType } from '@/componentsDS/Chips/Chips';
import Dropdown from '@/componentsDS/Dropdown/Dropdown';
import Icon from '@/componentsDS/Icon/Icon';
import InputCaption from '@/componentsDS/Input/InputCaption';
import InputLabel from '@/componentsDS/Input/InputLabel';
import Tooltip from '@/componentsDS/Tooltip';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { forwardRef, useEffect, useState } from 'react';

const Input = forwardRef(({
	id,
	name,
	type = 'text',
	label,
	placeholder,
	disabled = false,
	required = false,
	errorMessage,
	infoText,
	captionText,
	value = '',
	onChange,
	onClick,
	variant = "outlined",
	fullWidth = false,
	size = "default",
	labelInfoText,
	addClassName,
	prefixIcon,
	suffixIcon,
	prefixDropdown,
	prefixDropdownOption,
	prefixDropdownValue,
	prefixDropdownSelectedOption,
	prefixDropdownOnAdd,
	suffixDropdown,
	suffixDropdownOption,
	suffixDropdownValue,
	suffixDropdownSelectedOption,
	suffixDropdownOnAdd,
	chipInputMultiple,
	chipValues,
	setChipValues,
	allowChipModify,
	onClearChips,
	onBlur,
	disableClear = false,
	register,
	pipeText,
	keyPress,
	setValue,
	...inputProps
}, ref) => {
	const [inputValue, setInputValue] = useState(value);

	useEffect(() => {
	setInputValue(value);
	}, [value]);

	const chipInput = chipValues ? true : false;

	function handleOnChange(e) {
		const newValue = e.target.value;
		if (onChange) {
			onChange(newValue);
		}

		setInputValue(newValue);
	}

	function handleOnClear(e) {
		e.stopPropagation();

		if (onChange) {
			onChange('');
		}

		setInputValue('');
		if (setValue) {
			setValue(name, '', { shouldValidate: true });
		}
	}

	function handleKeyPress(e) {
		if (e.key === 'Enter' && inputValue && chipInput && !chipInputMultiple && allowChipModify) {
			e.preventDefault();
			allowChipModify(inputValue);
			setInputValue('');
		}

		if (keyPress)
			keyPress(e);
	}

	function handleClearChip(id) {
		const newChipValues = chipValues?.filter((chip) => chip.id !== id) || [];
		if (setChipValues) {
			setChipValues(newChipValues);
		}
	}

	function handleClearAllChip() {
		if (setChipValues) {
			setChipValues([]);
		}
		if (onClearChips) {
			onClearChips();
		}
	}

	const inputClass = classNames('DS__input', addClassName, {
		'DS__input--fullWidth': fullWidth,
		'DS__input--small': size === "small" && !chipInput,
		'DS__input--disabled': disabled,
		'DS__input--error': errorMessage,
	});

	const inputContainerClass = classNames({
		'DS__input-container': !chipInput,
		'DS__chip-input-container': chipInput && !chipInputMultiple,
		'DS__chip-input-container--multiple': chipInput && chipInputMultiple,
		'DS__input-container--outlined': variant === "outlined",
		'DS__input-container--filled': variant === "filled",
		'DS__input-container--with-value': inputValue,
		'DS__input-container--error': errorMessage,
		'DS__input-container--info': infoText,
		'DS__input-container--disabled': disabled
	});

	const inputFieldClass = classNames({
		'DS__input-field': !chipInput,
		'DS__chip-input-field': chipInput,
		'DS__input-field--with-prefix': prefixDropdown,
		'DS__input-field--with-suffix': (suffixIcon && inputValue) || suffixDropdown
	});

	const inputLabelClass = classNames('DS__input-label', {
		'DS__input-label--disabled': (!chipInput && !inputValue && disabled) || (chipInput && disabled && chipValues?.length === 0)
	});

	const placeHolderText = (!chipInputMultiple && (chipValues?.length ?? 0) > 0) || (chipInputMultiple && disabled && (chipValues?.length ?? 0) > 0) ? '' : placeholder || 'Enter Text';

	return (
		<div className={inputClass} data-testid="input-field">
			{label && <InputLabel id={id} className={inputLabelClass} label={label} required={required} labelInfoText={labelInfoText} />}
			<div className={inputContainerClass}>
				{prefixIcon && <div className="DS__input__prefix-icon">{prefixIcon}</div>}
				{prefixDropdown && (
					<Dropdown
						options={prefixDropdownOption}
						value={prefixDropdownSelectedOption}
						onAdd={prefixDropdownOnAdd}
						trigger={
							<div className="DS__input__prefix-dropdown" data-testid="input-prefix-dropdown">
								<span>{prefixDropdownValue}</span>
								<Icon name="chevron-down" style="solid" size="small" />
							</div>
						}
						multiple={false}
						disabled={disabled}
						offset={size === "small" ? [0, -2] : [0, 4]}
						size={size}
					/>
				)}

				{chipInput &&
					(chipInputMultiple && chipValues?.length ? (
						chipValues.length === 1 ? (
							<Chips
								label={chipValues[0].label}
								size="small"
								onRemove={() => handleClearAllChip()}
								readOnly={disabled}
								type={ChipType.INPUT}
							/>
						) : (
							<Chips
								label={`${chipValues.length} Selected`}
								size="small"
								onRemove={() => handleClearAllChip()}
								readOnly={disabled}
								type={ChipType.INPUT}
							/>
						)
					) : (
						chipValues?.map((chip) => <Chips key={chip.id} label={chip.label} size="small" onRemove={() => handleClearChip(chip.id)} readOnly={disabled} type={ChipType.INPUT} />)
					))
				}

				<div className={inputFieldClass}>
					<input
						id={id}
						name={name}
						{...(!register && { value: inputValue })}
						type={type}
						placeholder={placeHolderText}
						readOnly={disabled}
						onChange={handleOnChange}
						onKeyDown={handleKeyPress}
						onClick={(e) => {
							e.stopPropagation();
							if (onClick) {
								onClick(e);
							}
						}}
						className="DS__input-field__input"
						data-testid="input-field__input"
						ref={register ?? ref}
						autoComplete="off"
						{...inputProps}
					/>
					{inputValue && !disabled && !disableClear && ((chipInput && chipInputMultiple) || !chipInput) && (
						<div className="DS__input-field__clear-btn">
							<Button type={ButtonType.UTILITY} variant={ButtonVariant.UTILITY} onClick={handleOnClear} text={<Icon name="x-mark" style="outlined" size="small" />} />
						</div>
					)}
				</div>

				{suffixIcon && <div className="DS__input__suffix-icon">{suffixIcon}</div>}
				{suffixDropdown && (
					<Dropdown
						options={suffixDropdownOption}
						value={suffixDropdownSelectedOption}
						onAdd={suffixDropdownOnAdd}
						trigger={
							<div className="DS__input__suffix-dropdown" data-testid="input-suffix-dropdown">
								{suffixDropdownValue}
								<Icon name="chevron-down" style="solid" size="small" />
							</div>
						}
						multiple={false}
						placement={'bottom-end'}
						offset={size === "small" ? [0, -2] : [0, 4]}
						disabled={disabled}
						size={size}
					/>
				)}
			</div>
			{pipeText && (
				<div className="input-field__pipe-text">
					<span>{pipeText}</span>
				</div>
			)}
			<InputCaption id={id} errorMessage={errorMessage} infoText={infoText} captionText={captionText} />
		</div>
	);
});

export default Input;

Input.propTypes = {
	id: PropTypes.string.isRequired,
	name: PropTypes.string,
	type: PropTypes.string,
	label: PropTypes.string,
	placeholder: PropTypes.string,
	disabled: PropTypes.bool,
	required: PropTypes.bool,
	errorMessage: PropTypes.string,
	infoText: PropTypes.string,
	captionText: PropTypes.string,
	value: PropTypes.string,
	onChange: PropTypes.func,
	onCancel: PropTypes.func,
	variant: PropTypes.oneOf(['outlined', 'filled']),
	fullWidth: PropTypes.bool,
	size: PropTypes.oneOf(['default', 'small']),
	labelInfoIcon: PropTypes.bool,
	addClassName: PropTypes.string,
	prefixIcon: PropTypes.element,
	suffixIcon: PropTypes.element,
	prefixDropdown: PropTypes.bool,
	prefixDropdownOption: PropTypes.arrayOf(PropTypes.object),
	prefixDropdownValue: PropTypes.string,
	prefixDropdownSelectedOption: PropTypes.object,
	prefixDropdownOnAdd: PropTypes.func,
	suffixDropdown: PropTypes.bool,
	suffixDropdownOption: PropTypes.arrayOf(PropTypes.object),
	suffixDropdownValue: PropTypes.string,
	suffixDropdownSelectedOption: PropTypes.object,
	suffixDropdownOnAdd: PropTypes.func,
	chipInputMultiple: PropTypes.bool,
	chipValues: PropTypes.arrayOf(PropTypes.object),
	onClearChips: PropTypes.func,
	setChipValues: PropTypes.func,
	allowChipModify: PropTypes.func,
	register: PropTypes.func,
	pipeText: PropTypes.string,
	keyPress: PropTypes.func,
	setValue: PropTypes.func,
	onClick: PropTypes.func,
	labelInfoText: PropTypes.string,
	onBlur: PropTypes.func,
	disableClear: PropTypes.bool
};
